import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search as SearchIcon, X, ArrowRight, Newspaper, Zap, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { servicesData } from '@/data/solutions';
import { getPressReleases } from '@/data/pressReleases';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

interface SearchResult {
  id: string | number;
  title: string;
  type: 'solution' | 'press';
  description: string;
  url: string;
  date?: string;
}

export default function Search({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t, language } = useLanguage();
  const pressReleases = getPressReleases(language);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onClose(); // This might need a toggle instead, but assuming Caller handles it
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchSolutions = Object.entries(servicesData).filter(([key, sol]: [string, any]) => {
      const source = `${sol.title} ${sol.subtitle} ${sol.description} ${sol.features?.join(' ') || ''}`.toLowerCase();
      return source.includes(query.toLowerCase());
    }).map(([key, sol]: [string, any]) => ({
      id: key,
      title: sol.title,
      type: 'solution' as const,
      description: sol.description,
      url: `/servicos/${key}`
    }));

    const searchPress = pressReleases.filter(pr => {
      const source = `${pr.title} ${pr.excerpt} ${pr.content} ${pr.category}`.toLowerCase();
      return source.includes(query.toLowerCase());
    }).map(pr => ({
      id: pr.id,
      title: pr.title,
      type: 'press' as const,
      description: pr.excerpt,
      url: `/sala-de-imprensa/press-releases/${pr.id}`,
      date: pr.date
    }));

    setResults([...searchSolutions, ...searchPress]);
  }, [query]);

  const handleResultClick = (url: string) => {
    navigate(url);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-background/80 backdrop-blur-md"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="w-full max-w-2xl bg-card border border-border shadow-2xl rounded-2xl overflow-hidden relative z-10"
          >
            <div className="p-4 border-b border-border flex items-center gap-3">
              <SearchIcon className="h-5 w-5 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                placeholder={t("Busque por soluções, press releases ou tecnologias...", "Busque soluciones, notas de prensa o tecnologías...", "Search for solutions, press releases or technologies...")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-grow bg-transparent border-none outline-none text-lg text-foreground placeholder:text-muted-foreground"
              />
              <button 
                onClick={onClose}
                className="p-1 hover:bg-secondary rounded-lg transition-colors"
                aria-label={t("Fechar busca", "Cerrar búsqueda", "Close search")}
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {query.trim() === '' ? (
                <div className="p-8 text-center">
                  <p className="text-muted-foreground">{t("Digite algo para iniciar a busca...", "Escriba algo para iniciar la búsqueda...", "Type something to start searching...")}</p>
                  <div className="flex flex-wrap justify-center gap-4 mt-6">
                    {['DDoS', 'DNS', 'Observabilidade', 'Expansão', 'MSSP'].map(s => (
                      <button 
                        key={s}
                        onClick={() => setQuery(s)}
                        className="px-3 py-1 bg-secondary text-secondary-foreground text-[10px] font-bold uppercase tracking-widest rounded-full hover:bg-primary hover:text-primary-foreground transition-all underline decoration-primary/30"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : results.length > 0 ? (
                <div className="space-y-1">
                  {results.map((res) => (
                    <button
                      key={`${res.type}-${res.id}`}
                      onClick={() => handleResultClick(res.url)}
                      className="w-full p-4 flex gap-4 text-left hover:bg-secondary/50 rounded-xl transition-colors group"
                    >
                      <div className="shrink-0 mt-1">
                        {res.type === 'solution' ? (
                          <Zap className="h-5 w-5 text-primary" />
                        ) : (
                          <Newspaper className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-primary/70">
                            {res.type === 'solution' ? t('Solução', 'Solución', 'Solution') : t('Sala de Imprensa', 'Sala de Prensa', 'Newsroom')}
                          </span>
                          {res.date && (
                            <span className="text-[9px] font-mono text-muted-foreground">{res.date}</span>
                          )}
                        </div>
                        <h4 className="font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
                          {res.title}
                        </h4>
                        <p className="text-xs text-muted-foreground line-clamp-1 leading-relaxed">
                          {res.description}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-all self-center" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <SearchIcon className="h-12 w-12 text-muted-foreground/20 mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium">{t(`Nenhum resultado encontrado para "${query}"`, `No se encontraron resultados para "${query}"`, `No results found for "${query}"`)}</p>
                  <p className="text-xs text-muted-foreground mt-2">{t("Tente termos mais genéricos ou verifique a ortografia.", "Intente con términos más genéricos o verifique la ortografía.", "Try more generic terms or check the spelling.")}</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-secondary/30 border-t border-border hidden md:flex justify-between items-center text-[10px] font-medium text-muted-foreground">
              <div className="flex gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-background border border-border rounded">ESC</kbd> {t("para fechar", "para cerrar", "to close")}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-background border border-border rounded">↵</kbd> {t("para selecionar", "para seleccionar", "to select")}
                </span>
              </div>
              <p>TechEnabler Search 2026</p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
